/* Promo-Overlay Web Component (DEV-2026-0116) — Lovable-Liquid-Glass-Optik.
 *
 * Liest URL-Param ?promocode=, matched gegen promo-overlay-data JSON,
 * rendert ein Liquid-Glass-Overlay (Refraktion via SVG-Filter #liquid-glass).
 * Persistenz pro Session via sessionStorage: einmal erkannter Code gilt fuer
 * jede weitere Seite desselben Tabs, auch ohne Parameter (DEV-2026-0297).
 * Sticky-ATC: das Overlay misst die ATC-Hoehe und schiebt sich entsprechend
 * nach oben (bottom-Offset via CSS-Variable --promo-bottom-offset).
 */
(function () {
  'use strict';

  function getParam(name) {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get(name);
    } catch { return null; }
  }

  /* DEV-2026-0297: Der erkannte Code soll den ganzen Tab-Besuch begleiten, nicht
   * nur die Landingpage — interne Links tragen ?promocode= nicht weiter.
   *
   * Bewusst sessionStorage: neuer Tab = neuer Besuch = kein Overlay. Der
   * Attribution-Tracker (snippets/attribution-tracker.liquid) haelt Kampagnen-
   * Parameter zusaetzlich 30 Tage in localStorage — das ist eine andere Aufgabe
   * (Zuordnung der spaeteren Bestellung) und darf hier NICHT als Quelle dienen,
   * sonst zeigen wir das Overlay Tage nach dem Klick noch an.
   */
  function codeKey(prefix) { return prefix + ':code'; }

  function rememberCode(prefix, code) {
    try { sessionStorage.setItem(codeKey(prefix), code); } catch {}
  }

  function recallCode(prefix) {
    try { return (sessionStorage.getItem(codeKey(prefix)) || '').trim(); }
    catch { return ''; }
  }

  function loadOverlays() {
    const node = document.getElementById('promo-overlay-data');
    if (!node) return [];
    try {
      const arr = JSON.parse(node.textContent || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  }

  function svgIcon(name) {
    if (name === 'copy') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>';
    }
    if (name === 'check') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
    }
    if (name === 'info') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
    }
    if (name === 'close') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    }
    return '';
  }

  class PromoOverlay extends HTMLElement {
    constructor() {
      super();
      this._handleResize = this._handleResize.bind(this);
      this._handleScroll = this._handleScroll.bind(this);
      this._rafId = null;
    }

    connectedCallback() {
      const storagePrefix = this.dataset.storagePrefix || 'promo-overlay-v1';
      const urlCode = (getParam('promocode') || '').trim();
      // Erst der guenstige Weg: ohne Code aus URL oder Session ist hier Schluss,
      // bevor wir das Metaobjekt-JSON parsen. Das ist der Normalfall jedes Aufrufs.
      const code = urlCode || recallCode(storagePrefix);
      if (!code) return;

      const all = loadOverlays();
      const data =
        all.find((o) => ((o && o.code) || '').trim().toLowerCase() === code.toLowerCase()) || null;

      if (urlCode) {
        // Nur gepflegte Codes duerfen die Session ueberdauern: ein Tippfehler in
        // der Kampagnen-URL soll nicht den ganzen Besuch begleiten. Auf der
        // Landingpage selbst zeigen wir ihn weiter an (Verhalten DEV-2026-0116).
        if (data) rememberCode(storagePrefix, urlCode);
      } else if (!data) {
        // Gemerkter Code ohne Metaobjekt (im Admin inzwischen entfernt) → nichts
        // anzeigen, statt eine inhaltsleere Karte durch die Session zu schleppen.
        return;
      }

      this._storageKey = storagePrefix + ':dismissed:' + code.toLowerCase();
      try {
        if (sessionStorage.getItem(this._storageKey) === 'dismissed') return;
      } catch {}

      const accent = this.dataset.accent || '#A01F46';
      this.style.setProperty('--promo-accent', accent);

      this._data = data || { code };

      this._render();
      this._adjustBottomOffset();
      window.addEventListener('resize', this._handleResize, { passive: true });
      window.addEventListener('scroll', this._handleScroll, { passive: true });

      requestAnimationFrame(() => {
        this.setAttribute('data-visible', 'true');
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._handleResize);
      window.removeEventListener('scroll', this._handleScroll);
      if (this._rafId) cancelAnimationFrame(this._rafId);
    }

    _handleResize() { this._adjustBottomOffset(); }
    _handleScroll() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this._adjustBottomOffset();
      });
    }

    /** Sticky-ATC erkennen und den bottom-offset so setzen, dass das Overlay
     *  darueber sitzt. Heuristik: alle elemente mit class*=sticky/atc/buy/cart
     *  die position:fixed sind und mit dem Viewport-Boden ueberlappen. */
    _adjustBottomOffset() {
      const candidates = document.querySelectorAll(
        '[class*="sticky" i][class*="atc" i], [class*="sticky" i][class*="buy" i], [class*="sticky-buy" i], [data-sticky-atc], [class*="add-to-cart-sticky" i]'
      );
      let stickyHeight = 0;
      const vh = window.innerHeight;
      candidates.forEach((el) => {
        if (el === this) return;
        const cs = getComputedStyle(el);
        if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
        if (cs.display === 'none' || cs.visibility === 'hidden') return;
        const r = el.getBoundingClientRect();
        if (r.height === 0) return;
        if (r.bottom >= vh - 4 && r.top < vh) {
          const h = Math.min(r.height, vh - r.top);
          if (h > stickyHeight) stickyHeight = h;
        }
      });
      const base = 16;
      this.style.setProperty('--promo-bottom-offset', (base + stickyHeight) + 'px');
    }

    _render() {
      const data = this._data;
      const code = data.code;
      const hasTitle = !!(data.title && data.title.trim());
      const hasInfo = !!(data.minimum_order || data.valid_until || data.scope);
      // DEV-2026-0153: Auto-Detect Incentive-Typ aus value_label + title:
      //  - Gratis-Versand → 🚚
      //  - Gratis-Artikel → 🎁
      //  - sonst: value_label (z.B. "15%") als Text-Badge (CSS macht's fett)
      const incentiveText = ((data.value_label || '') + ' ' + (data.title || '')).toLowerCase();
      let badgeContent;
      let badgeKind = 'value';
      if (/gratis[\s-]*versand|kostenloser?\s*versand|free\s*shipping/.test(incentiveText)) {
        badgeContent = '🚚';
        badgeKind = 'shipping';
      } else if (/gratis[\s-]*artikel|gratis[\s-]*produkt|geschenk|free\s*gift|🎁/.test(incentiveText)) {
        badgeContent = '🎁';
        badgeKind = 'gift';
      } else {
        badgeContent = (data.value_label && data.value_label.trim()) || '%';
      }

      const popoverItems = [];
      if (data.minimum_order) popoverItems.push('Mindestbestellwert: ' + data.minimum_order);
      if (data.valid_until) popoverItems.push('Gültig bis: ' + data.valid_until);
      if (data.scope) popoverItems.push(data.scope);

      const wrap = document.createElement('div');
      wrap.className = 'po-wrap';

      wrap.innerHTML = [
        hasInfo ? '<div class="po-popover" role="tooltip">' +
          '<div class="po-popover-title">Gutschein-Details</div>' +
          '<ul class="po-popover-list">' +
            popoverItems.map(function (t) { return '<li>' + escapeHtml(t) + '</li>'; }).join('') +
          '</ul>' +
        '</div>' : '',
        '<div class="po-card" role="dialog" aria-label="Rabattcode aktiviert">',
          '<div class="po-badge" data-kind="' + badgeKind + '" aria-hidden="true">', escapeHtml(badgeContent), '</div>',
          '<div class="po-text">',
            hasTitle ? '<div class="po-title">' + escapeHtml(data.title) + '</div>' : '',
            '<div class="po-code">', escapeHtml(code), '</div>',
          '</div>',
          '<div class="po-actions">',
            '<button type="button" class="po-copy" aria-label="Code kopieren" title="Code kopieren">',
              svgIcon('copy'),
            '</button>',
            hasInfo ? '<button type="button" class="po-icon-btn po-info" aria-label="Gutschein-Details" aria-expanded="false">' + svgIcon('info') + '</button>' : '',
            '<button type="button" class="po-icon-btn po-min" aria-label="Minimieren">', svgIcon('close'), '</button>',
          '</div>',
        '</div>',
        '<button type="button" class="po-mini" data-kind="' + badgeKind + '" aria-label="Gutschein anzeigen">', escapeHtml(badgeContent), '</button>',
      ].join('');

      this.appendChild(wrap);
      this._wrap = wrap;

      const copyBtn = wrap.querySelector('.po-copy');
      const minBtn = wrap.querySelector('.po-min');
      const miniBtn = wrap.querySelector('.po-mini');
      const infoBtn = wrap.querySelector('.po-info');
      const popover = wrap.querySelector('.po-popover');

      copyBtn.addEventListener('click', () => this._copy(copyBtn));

      minBtn.addEventListener('click', () => {
        if (popover) popover.setAttribute('data-open', 'false');
        this.setAttribute('data-minimized', 'true');
      });
      miniBtn.addEventListener('click', () => {
        this.removeAttribute('data-minimized');
      });

      if (infoBtn && popover) {
        infoBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const open = popover.getAttribute('data-open') === 'true';
          popover.setAttribute('data-open', open ? 'false' : 'true');
          infoBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
        document.addEventListener('click', (e) => {
          if (!wrap.contains(e.target)) {
            popover.setAttribute('data-open', 'false');
            infoBtn.setAttribute('aria-expanded', 'false');
          }
        });
      }
    }

    async _copy(copyBtn) {
      const code = this._data.code;
      try {
        await navigator.clipboard.writeText(code);
      } catch {
        const tmp = document.createElement('textarea');
        tmp.value = code;
        tmp.style.position = 'fixed';
        tmp.style.top = '-9999px';
        document.body.appendChild(tmp);
        tmp.select();
        try { document.execCommand('copy'); } catch {}
        document.body.removeChild(tmp);
      }
      copyBtn.dataset.copied = 'true';
      copyBtn.setAttribute('aria-label', 'Code wurde kopiert');
      copyBtn.setAttribute('title', 'Kopiert!');
      const svg = copyBtn.querySelector('svg');
      if (svg) svg.outerHTML = svgIcon('check');
      setTimeout(() => {
        copyBtn.dataset.copied = 'false';
        copyBtn.setAttribute('aria-label', 'Code kopieren');
        copyBtn.setAttribute('title', 'Code kopieren');
        const s = copyBtn.querySelector('svg');
        if (s) s.outerHTML = svgIcon('copy');
      }, 1800);
    }
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  if (!customElements.get('promo-overlay')) {
    customElements.define('promo-overlay', PromoOverlay);
  }
})();
